import { Search } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { getGroupsList } from "actions/groups";
import { getOrganisationsList } from "actions/organisation";
import { USER_TYPE } from "components/UserPage/utils";
import { REGULATORY_CONTROL_OPTIONS, SITE_LIST_FITLERS } from "constants/site";
import { CONTROL_MODE_STATUS_OPTIONS, SITE_VIEW_OPTIONS } from "constants/unit";
import { FormField } from "empower-energy-owner-components";
import useDebounce from "hooks/useDebounce";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const styles = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  alignItems: "flex-end",

  "& .mui-field": {
    minWidth: "185px",
    maxWidth: "210px",
    flex: "1 1 185px",
  },

  "@media (max-width: 1200px)": {
    "& .mui-field": {
      flex: "1 1 calc(33% - 12px)",
    },
  },
  "@media (max-width: 768px)": {
    "& .mui-field": {
      flex: "1 1 calc(50% - 12px)",
    },
  },
  "@media (max-width: 500px)": {
    "& .mui-field": {
      flex: "1 1 100%",
    },
  },
};

const SiteFilter = ({ onChangeFilters, filters }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(filters);

  const profile = useSelector((state) => state.profile);
  const dnspOptions = useSelector((state) => state.option?.dnsp || []);
  const organisationList = useSelector(
    (state) => state.organisation?.list || [],
  );
  const installerList = useSelector(
    (state) => state.organisation?.installerList || [],
  );
  const groupList = useSelector((state) => state.groups?.list || []);
  const channelOptions = useSelector((state) => state.option?.channel || []);
  const lifecycleStateList = useSelector(
    (state) => state.option?.lifecycle_state || [],
  );
  const meterList = useSelector((state) => state.option?.meter_model || []);

  const debouncedSearch = useDebounce(formData.search, 500);

  useEffect(() => {
    dispatch(getGroupsList());

    if (profile?.is_staff) {
      dispatch(getOrganisationsList());
      dispatch(getOrganisationsList(USER_TYPE.INSTALLER));
    }
  }, [profile?.is_staff, dispatch]);

  useEffect(() => {
    setFormData(filters);
  }, [filters]);

  useEffect(() => {
    if (filters.search !== debouncedSearch) {
      onChangeFilters({ ...formData, search: debouncedSearch });
    }
  }, [debouncedSearch]);

  const constructOptions = useCallback(
    (list = []) => [
      { value: "", label: "Select All" },
      ...list.filter((o) => o?.value !== "" && o?.label),
    ],
    [],
  );

  const stateOptions = useMemo(() => {
    const states = [...new Set(dnspOptions?.map((it) => it.state_name))];
    return states.map((it) => ({
      value: it,
      label: it,
    }));
  }, [dnspOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        search: "",
        paginationModel: {
          pageSize: prev?.paginationModel.pageSize,
          page: 0,
        },
        [name]: value,
      };

      if (name !== "search") {
        onChangeFilters(updated);
      }
      return updated;
    });
  };

  const handleSearchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      ...SITE_LIST_FITLERS,
      view: prev.view,
      search: e.target.value,
    }));
  };

  const renderSelect = (label, name, list) => (
    <FormField
      className="mui-field"
      label={label}
      name={name}
      type="select"
      variant="outlined"
      value={formData[name] ?? ""}
      onChange={handleChange}
      options={constructOptions(list)}
    />
  );

  return (
    <Box my={2}>
      <Box className="items-center row-wrap" sx={styles}>
        {profile?.is_staff &&
          renderSelect("Select Organisation", "organisation", organisationList)}

        {renderSelect("Select Group", "group", groupList)}

        {profile?.is_staff &&
          renderSelect("Select Installer", "installer", installerList)}

        {renderSelect("Select DNSP", "dnsp", dnspOptions)}
        {renderSelect("Select State", "state", stateOptions)}

        {profile?.is_staff &&
          renderSelect("Sales Channel", "channel", [
            { label: "None", value: "none" },
            ...channelOptions,
          ])}

        {renderSelect("Select Lifecycle", "lifecycle", lifecycleStateList)}

        {renderSelect(
          "Select Meter",
          "meter",
          meterList.map((m) => ({ label: m.label, value: m.label })),
        )}

        {renderSelect(
          "Control Mode",
          "control_mode_status",
          CONTROL_MODE_STATUS_OPTIONS,
        )}

        {renderSelect(
          "Regulatory Control",
          "regulatory_control",
          REGULATORY_CONTROL_OPTIONS,
        )}

        <Box sx={{ flexGrow: 1 }} />

        {profile?.is_staff && (
          <Box mt={-2} mr={2}>
            <Link
              variant="subtitle2"
              onClick={() => navigate("/sites/install-report")}
              sx={{ cursor: "pointer" }}
              underline="always"
            >
              Install Report
            </Link>
          </Box>
        )}

        <TextField
          size="small"
          className="mui-field"
          label="Search"
          variant="outlined"
          value={formData.search}
          name="search"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <FormControl sx={{ mt: 1 }}>
        <RadioGroup
          row
          name="view"
          value={formData.view}
          onChange={handleChange}
        >
          {SITE_VIEW_OPTIONS.map((o) => (
            <FormControlLabel
              key={o.value}
              value={o.value}
              control={<Radio />}
              label={o.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default SiteFilter;
